import { PortCapability } from '../types/portCapabilities';
import { getCatalogDeviceModel } from './deviceModels';
import { collectLayoutPorts } from '../engine/networkLogic';

function capabilitiesFromModel(equipmentId: string): PortCapability[] | undefined {
  const model = getCatalogDeviceModel(equipmentId);
  if (!model) return undefined;
  return collectLayoutPorts(model.layout).map((port) => {
    const fiber = port.kind === 'sfp' || port.kind === 'sfpPlus';
    const std = port.poeOut?.standards[0] || port.poeIn?.standards[0];
    const ieee = std === '802.3af' || std === '802.3at' || std === '802.3bt' ? std : undefined;
    return {
      power: {
        poe: !!(port.poeOut || port.poeIn),
        poeStandard: ieee,
        voltage: port.poeIn?.voltageMin || (port.poeOut?.standards.includes('passive-24' as any) ? 24 : 48),
        wattage: port.poeOut?.perPortMaxWatts || port.poeIn?.maxWatts,
        editable: true,
      },
      speed: { ethernet: port.speedsMbps || [10, 100, 1000], fiber, sfp: port.kind === 'sfp', sfpPlus: port.kind === 'sfpPlus', editable: true },
      physical: { type: fiber ? (port.kind === 'sfpPlus' ? 'sfp+' : 'sfp') : 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: !fiber, fiber, console: false, usb: false, editable: true },
      purpose: port.poeIn && !port.poeOut ? 'uplink' : 'data',
      editable: true,
    } as PortCapability;
  });
}

export function getEquipmentPortCapabilities(equipmentId: string): PortCapability[] | undefined {
  return equipmentPortDatabase[equipmentId] || capabilitiesFromModel(equipmentId);
}

// Base equipment port database - can be overridden by user
export const equipmentPortDatabase: Record<string, PortCapability[]> = {
  // ===== UBIQUITI SWITCHES =====
  
  // UniFi Switch 8 (No PoE) - All ports editable
  'ubnt-us-8': Array(8).fill({
    power: { poe: false, editable: true },
    speed: { ethernet: [10, 100, 1000], editable: true },
    physical: { type: 'rj45', duplex: 'auto', editable: true },
    protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
    purpose: 'data',
    editable: true
  }) as PortCapability[],
  
  // UniFi Switch 16 PoE - Mixed PoE/non-PoE ports, all editable
  'ubnt-us-16-poe': [
    // Ports 1-8: PoE+ (802.3at) - editable
    ...Array(8).fill({
      power: { 
        poe: true, 
        poeStandard: '802.3at',
        voltage: 48,
        wattage: 15.4,
        maxWattage: 95,
        editable: true
      },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'data',
      editable: true
    }),
    // Ports 9-16: No PoE - editable
    ...Array(8).fill({
      power: { poe: false, editable: true },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'data',
      editable: true
    })
  ] as PortCapability[],
  
  // UniFi Switch 24 PoE
  'ubnt-us-24-poe': [
    // Ports 1-16: PoE+ (802.3at)
    ...Array(16).fill({
      power: { 
        poe: true, 
        poeStandard: '802.3at',
        voltage: 48,
        wattage: 15.4,
        maxWattage: 95,
        editable: true
      },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'data',
      editable: true
    }),
    // Ports 17-24: No PoE
    ...Array(8).fill({
      power: { poe: false, editable: true },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'data',
      editable: true
    })
  ] as PortCapability[],
  
  // UniFi Switch Pro 24 PoE (802.3bt support)
  'ubnt-us-24-pro-poe': [
    // Ports 1-12: PoE++ (802.3bt)
    ...Array(12).fill({
      power: { 
        poe: true, 
        poeStandard: '802.3bt',
        voltage: 48,
        wattage: 30,
        maxWattage: 400,
        editable: true
      },
      speed: { ethernet: [10, 100, 1000, 2500], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'data',
      editable: true
    }),
    // Ports 13-24: PoE+ (802.3at)
    ...Array(12).fill({
      power: { 
        poe: true, 
        poeStandard: '802.3at',
        voltage: 48,
        wattage: 15.4,
        maxWattage: 400,
        editable: true
      },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'data',
      editable: true
    })
  ] as PortCapability[],
  
  // ===== UBIQUITI ACCESS POINTS =====
  
  // UniFi 6 Professional AP (Single port)
  'ubnt-u6-pro': [
    {
      power: { 
        poe: true, 
        poeStandard: '802.3at',
        voltage: 48,
        wattage: 13,
        editable: true
      },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'uplink',
      editable: true
    }
  ],
  
  // UniFi 6 Long Range AP (Single port)
  'ubnt-u6-lr': [
    {
      power: { 
        poe: true, 
        poeStandard: '802.3at',
        voltage: 48,
        wattage: 16.5,
        editable: true
      },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'uplink',
      editable: true
    }
  ],
  
  // UniFi 6 Enterprise AP (Single port, 2.5G)
  'ubnt-u6-enterprise': [
    {
      power: { 
        poe: true, 
        poeStandard: '802.3bt',
        voltage: 48,
        wattage: 20,
        editable: true
      },
      speed: { ethernet: [10, 100, 1000, 2500], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'uplink',
      editable: true
    }
  ],
  
  // UniFi 6 Mesh AP (Single port)
  'ubnt-u6-mesh': [
    {
      power: { 
        poe: true, 
        poeStandard: '802.3at',
        voltage: 48,
        wattage: 12,
        editable: true
      },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'uplink',
      editable: true
    }
  ],
  
  // ===== UBIQUITI ROUTERS =====
  
  // UniFi Dream Machine (Multiple ports)
  'ubnt-udm': [
    // WAN port
    {
      power: { poe: false, editable: true },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'uplink',
      editable: true
    },
    // LAN ports 1-8
    ...Array(8).fill({
      power: { poe: false, editable: true },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'downlink',
      editable: true
    })
  ],
  
  // ===== TP-LINK EQUIPMENT =====
  
  // TP-Link TL-SG108 (8-port switch, no PoE)
  'tplink-tl-sg108': Array(8).fill({
    power: { poe: false, editable: true },
    speed: { ethernet: [10, 100, 1000], editable: true },
    physical: { type: 'rj45', duplex: 'auto', editable: true },
    protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
    purpose: 'data',
    editable: true
  }) as PortCapability[],
  
  // TP-Link TL-SG1016PE (16-port PoE switch)
  'tplink-tl-sg1016pe': [
    // Ports 1-8: PoE+ (802.3at)
    ...Array(8).fill({
      power: { 
        poe: true, 
        poeStandard: '802.3at',
        voltage: 48,
        wattage: 15.4,
        maxWattage: 150,
        editable: true
      },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'data',
      editable: true
    }),
    // Ports 9-16: No PoE
    ...Array(8).fill({
      power: { poe: false, editable: true },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'data',
      editable: true
    })
  ] as PortCapability[],
  
  // TP-Link EAP660 HD (Wi-Fi 6 AP)
  'tplink-eap660-hd': [
    {
      power: { 
        poe: true, 
        poeStandard: '802.3at',
        voltage: 48,
        wattage: 16.5,
        editable: true
      },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'uplink',
      editable: true
    }
  ],
  
  // ===== MIKROTIK EQUIPMENT =====
  
  // MikroTik hEX S (5-port router)
  'mikrotik-hex-s': [
    // WAN port
    {
      power: { poe: false, editable: true },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'uplink',
      editable: true
    },
    // LAN ports 1-4
    ...Array(4).fill({
      power: { poe: false, editable: true },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'downlink',
      editable: true
    })
  ],
  
  // ===== CAMERAS =====
  
  // UniFi G4 Bullet Camera
  'ubnt-g4-bullet': [
    {
      power: { 
        poe: true, 
        poeStandard: '802.3af',
        voltage: 48,
        wattage: 4,
        editable: true
      },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'uplink',
      editable: true
    }
  ],
  
  // UniFi G4 Pro Camera
  'ubnt-g4-pro': [
    {
      power: { 
        poe: true, 
        poeStandard: '802.3at',
        voltage: 48,
        wattage: 12.5,
        editable: true
      },
      speed: { ethernet: [10, 100, 1000], editable: true },
      physical: { type: 'rj45', duplex: 'auto', editable: true },
      protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
      purpose: 'uplink',
      editable: true
    }
  ],

  // ===== TP-LINK ACCESS POINTS =====
  
  // TP-Link EAP225 - Single PoE port
  'tplink-eap225': [{
    power: { 
      poe: true, 
      poeStandard: '802.3af',
      voltage: 48,
      wattage: 12.95,
      maxWattage: 12.95,
      editable: true
    },
    speed: { ethernet: [10, 100, 1000], editable: true },
    physical: { type: 'rj45', duplex: 'auto', editable: true },
    protocols: { ethernet: true, fiber: false, console: false, usb: false, editable: true },
    purpose: 'data',
    editable: true
  }]
};

// Equipment editing utilities
export class EquipmentEditor {
  // Get editable equipment with user modifications
  static getEditableEquipment(equipmentId: string, userModifications?: any): any {
    const baseEquipment = this.getBaseEquipment(equipmentId);
    
    return {
      id: equipmentId,
      name: baseEquipment.name,
      type: baseEquipment.type,
      manufacturer: baseEquipment.manufacturer,
      baseSpecifications: baseEquipment.specifications,
      customSpecifications: userModifications?.specifications,
      customPorts: userModifications?.ports,
      isCustom: userModifications?.isCustom || false,
      isModified: !!userModifications,
      lastModified: userModifications?.lastModified
    };
  }
  
  // Get base equipment from database
  private static getBaseEquipment(equipmentId: string): any {
    // This would integrate with the main equipment database
    // Placeholder for now
    return {
      id: equipmentId,
      name: 'Base Equipment',
      type: 'switch',
      manufacturer: 'ubiquiti',
      specifications: {}
    };
  }
  
  // Validate equipment modifications
  static validateEquipmentModifications(equipment: any): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    // Validate port count
    if (equipment.customSpecifications?.ports && equipment.customSpecifications.ports < 1) {
      errors.push('Equipment must have at least 1 port');
    }
    
    // Validate PoE specifications
    if (equipment.customSpecifications?.power) {
      // Add PoE validation logic
    }
    
    // Validate custom ports
    if (equipment.customPorts) {
      equipment.customPorts.forEach((port: any, index: number) => {
        if (!port.capability) {
          errors.push(`Port ${index + 1} must have capability specifications`);
        }
      });
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
  
  // Save equipment modifications
  static saveEquipmentModifications(equipment: any): void {
    // Placeholder for saving to local storage or backend
    console.log('Saving equipment modifications:', equipment);
  }
  
  // Reset equipment to defaults
  static resetEquipmentToDefaults(equipmentId: string): any {
    return this.getEditableEquipment(equipmentId);
  }
}
